import React from 'react';
import { Button, Dialog, Flex } from '@/components/ui';
import { FilePreviewModal } from '@/components/file-preview';
import { getCsvFileDownloadEndpoint } from '@/utils/downloadEndpoints';
import { FormDetailsModal } from './FormDetailsModal';
import { ColumnClassificationModal } from './ColumnClassificationModal';
import { ColumnSelectionModal } from './ColumnSelectionModal';
import { FormFieldSelectionModal } from './FormFieldSelectionModal';
import {
  ColumnMapping,
  ColumnMappingType,
  SelectedFile,
  StudyForm,
} from './types';

interface ModalsContainerProps {
  // File Preview Modal
  previewModalOpen: boolean;
  setPreviewModalOpen: (open: boolean) => void;
  file: SelectedFile;
  fileName: string;

  // Form Details Modal
  formDetailsModalOpen: boolean;
  setFormDetailsModalOpen: (open: boolean) => void;
  selectedForm: StudyForm | null;

  // Column Classification Modal
  columnClassifyModalOpen: boolean;
  setColumnClassifyModalOpen: (open: boolean) => void;
  selectedColumnForClassify: string | null;
  getFilesWithColumn: (columnName: string) => number;
  isSelectedFormSubform: boolean;
  onColumnClassifyAsMetadata: (metadataType: string) => void;
  onColumnClassifyAsFormData: () => void;

  // Column Selection Modal for Required Metadata
  columnSelectionModalOpen: boolean;
  setColumnSelectionModalOpen: (open: boolean) => void;
  selectedMetadataForColumn: { type: string; label: string } | null;
  getAvailableColumnsForMetadata: Array<{
    name: string;
    isAlreadyMapped: boolean;
    currentMapping?: ColumnMapping;
  }>;
  onColumnSelectForMetadata: (columnName: string, applyToAll?: boolean) => void;
  onRemoveMetadataMapping: () => void;
  fileMappings: ColumnMapping[];
  fileId: string;

  // Form Field Selection Modal
  formFieldSelectionModalOpen: boolean;
  setFormFieldSelectionModalOpen: (open: boolean) => void;
  selectedColumnForFormField: string | null;
  selectedFieldForMapping: string | null;
  onFormFieldSelect: (selectedItem: string) => void;
  onRemoveFormFieldMapping: () => void;

  // Bulk Mapping Confirmation Dialog
  bulkMappingDialogOpen: boolean;
  setBulkMappingDialogOpen: (open: boolean) => void;
  pendingMappingChange: {
    columnName: string;
    mappingType: ColumnMappingType;
    targetField?: string;
    similarMappingsCount: number;
  } | null;
  handleBulkMappingResponse: (applyToAll: boolean) => void;
  handleBulkMappingCancel: () => void;
}

export const ModalsContainer: React.FC<ModalsContainerProps> = ({
  // File Preview Modal
  previewModalOpen,
  setPreviewModalOpen,
  file,
  fileName,

  // Form Details Modal
  formDetailsModalOpen,
  setFormDetailsModalOpen,
  selectedForm,

  // Column Classification Modal
  columnClassifyModalOpen,
  setColumnClassifyModalOpen,
  selectedColumnForClassify,
  getFilesWithColumn,
  isSelectedFormSubform,
  onColumnClassifyAsMetadata,
  onColumnClassifyAsFormData,

  // Column Selection Modal for Required Metadata
  columnSelectionModalOpen,
  setColumnSelectionModalOpen,
  selectedMetadataForColumn,
  getAvailableColumnsForMetadata,
  onColumnSelectForMetadata,
  onRemoveMetadataMapping,
  fileMappings,
  fileId,

  // Form Field Selection Modal
  formFieldSelectionModalOpen,
  setFormFieldSelectionModalOpen,
  selectedColumnForFormField,
  selectedFieldForMapping,
  onFormFieldSelect,
  onRemoveFormFieldMapping,

  // Bulk Mapping Confirmation Dialog
  bulkMappingDialogOpen,
  setBulkMappingDialogOpen,
  pendingMappingChange,
  handleBulkMappingResponse,
  handleBulkMappingCancel,
}) => {
  const apiEndpoint =
    file.source === 'csv-importer' && file.csvFile
      ? getCsvFileDownloadEndpoint(file.csvFile)
      : undefined;
  const fileUrl =
    file.source === 'uploaded' && file.file
      ? URL.createObjectURL(file.file)
      : undefined;

  return (
    <>
      {/* File Preview Modal */}
      <FilePreviewModal
        apiEndpoint={apiEndpoint}
        fileUrl={fileUrl}
        filename={fileName}
        mimeType="text/csv"
        fileSize={file.metadata.size}
        open={previewModalOpen}
        onOpenChange={setPreviewModalOpen}
        title={`Preview: ${fileName}`}
        showDownloadButton={false}
      />

      {/* Form Details Modal */}
      <FormDetailsModal
        form={selectedForm}
        open={formDetailsModalOpen}
        onOpenChange={setFormDetailsModalOpen}
      />

      {/* Column Classification Modal */}
      <ColumnClassificationModal
        columnName={selectedColumnForClassify || ''}
        filesCount={
          selectedColumnForClassify
            ? getFilesWithColumn(selectedColumnForClassify)
            : 0
        }
        open={columnClassifyModalOpen}
        onOpenChange={setColumnClassifyModalOpen}
        onClassifyAsMetadata={onColumnClassifyAsMetadata}
        onClassifyAsFormData={onColumnClassifyAsFormData}
        isSubform={isSelectedFormSubform}
      />

      {/* Column Selection Modal for Required Metadata */}
      <ColumnSelectionModal
        metadataType={selectedMetadataForColumn?.type || ''}
        metadataLabel={selectedMetadataForColumn?.label || ''}
        availableColumns={getAvailableColumnsForMetadata}
        open={columnSelectionModalOpen}
        onOpenChange={setColumnSelectionModalOpen}
        onColumnSelect={onColumnSelectForMetadata}
        onRemoveMapping={onRemoveMetadataMapping}
        currentMappedColumn={
          selectedMetadataForColumn
            ? fileMappings.find(
                mapping =>
                  mapping.mappingType === 'metadata' &&
                  mapping.metadataType?.type === selectedMetadataForColumn.type
              )?.columnName
            : undefined
        }
        similarFilesCount={0} // TODO: Calculate actual count of similar files
      />

      {/* Form Field Selection Modal for Columns */}
      <FormFieldSelectionModal
        fieldDataPoint={selectedFieldForMapping || undefined}
        columnName={selectedColumnForFormField || undefined}
        formFields={selectedForm?.fields || []}
        availableColumns={getAvailableColumnsForMetadata}
        open={formFieldSelectionModalOpen}
        onOpenChange={setFormFieldSelectionModalOpen}
        onFieldSelect={onFormFieldSelect}
        onRemoveMapping={onRemoveFormFieldMapping}
        currentMappedField={
          selectedColumnForFormField
            ? // Opening from column - show mapped field name
              fileMappings.find(
                m =>
                  m.fileId === fileId &&
                  m.columnName === selectedColumnForFormField &&
                  m.mappingType === 'formField'
              )?.targetField
            : // Opening from field - show mapped column name
              fileMappings.find(
                m =>
                  m.fileId === fileId &&
                  m.mappingType === 'formField' &&
                  m.targetField === selectedFieldForMapping
              )?.columnName
        }
      />

      {/* Bulk Mapping Confirmation Dialog */}
      <Dialog.Root
        open={bulkMappingDialogOpen}
        onOpenChange={setBulkMappingDialogOpen}
      >
        <Dialog.Content size="medium" style={{ maxWidth: '400px' }}>
          <Dialog.Header
            title="Apply Mapping Change"
            subtitle={`You are changing the mapping for column "${pendingMappingChange?.columnName}". There are ${pendingMappingChange?.similarMappingsCount} similar mappings in other files. How would you like to proceed?`}
          />
          <Dialog.Body>
            <Flex direction="column" gap="3">
              <Button
                variant="outlined"
                onClick={() => handleBulkMappingResponse(false)}
              >
                Apply only to this file
              </Button>
              <Button
                variant="contained"
                onClick={() => handleBulkMappingResponse(true)}
              >
                Apply to all {pendingMappingChange?.similarMappingsCount}{' '}
                similar mappings
              </Button>
              <Button
                variant="outlined"
                color="default"
                onClick={handleBulkMappingCancel}
              >
                Cancel
              </Button>
            </Flex>
          </Dialog.Body>
        </Dialog.Content>
      </Dialog.Root>
    </>
  );
};
